import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminShell } from '@/apps/coach/components/admin/AdminShell';
import { routes } from '@/lib/routes';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import type { CoachInvite } from '@/types/coach';

export function CoachInviteList() {
  const navigate = useNavigate();
  const [list, setList] = useState<CoachInvite[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [code, setCode] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    try {
      const data = await api.operator.coachInvites.list();
      setList(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler');
    }
  }

  useEffect(() => { load(); }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.operator.coachInvites.create({
        code: code.trim() || undefined,
        expiresAt: expiresAt || null,
        note: note.trim() || null,
      });
      toast.success('Coach-Invite erstellt.');
      setCode('');
      setExpiresAt('');
      setNote('');
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Fehler');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRevoke(invite: CoachInvite) {
    if (!confirm(`Invite ${invite.code} widerrufen?`)) return;
    try {
      await api.operator.coachInvites.revoke(invite.code);
      toast.success('Widerrufen.');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Fehler');
    }
  }

  function statusLabel(invite: CoachInvite): string {
    if (invite.revokedAt) return 'Widerrufen';
    if (invite.redeemedAt) return 'Eingelöst';
    if (invite.expiresAt && new Date(invite.expiresAt) < new Date()) return 'Abgelaufen';
    return 'Offen';
  }

  return (
    <AdminShell pageTitle="Coaches">
      <PageHeader title="Coach-Invites" showBack />
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
        {!showForm && <Button onClick={() => setShowForm(true)}>Neuer Invite</Button>}
        {showForm && (
          <form onSubmit={handleCreate} className="space-y-3 rounded-xl border border-border px-4 py-4">
            <label className="block space-y-1">
              <span className="text-sm font-medium">Code (optional)</span>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Leer lassen für Zufallscode"
                className="font-mono"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-sm font-medium">Ablaufdatum (optional)</span>
              <Input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </label>
            <label className="block space-y-1">
              <span className="text-sm font-medium">Notiz (optional)</span>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder='z.B. "Für Anna M., Karriere-Coaching"'
                rows={2}
                className="text-sm resize-none"
              />
            </label>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Erstelle…' : 'Erstellen'}
              </Button>
            </div>
          </form>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}
        {list?.length === 0 && !showForm && (
          <p className="text-sm text-muted-foreground">Noch keine Coach-Invites.</p>
        )}

        {list?.map((invite) => {
          const status = statusLabel(invite);
          const isOpen = status === 'Offen';
          return (
            <div key={invite.code} className="rounded-xl border border-border px-4 py-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-mono text-sm font-semibold">{invite.code}</p>
                  {invite.note && (
                    <p className="text-xs text-muted-foreground">{invite.note}</p>
                  )}
                </div>
                {isOpen ? (
                  <Button size="sm" variant="ghost" onClick={() => handleRevoke(invite)}>
                    Widerrufen
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground italic">{status}</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Erstellt am {new Date(invite.createdAt).toLocaleDateString('de-DE')}
                {invite.expiresAt &&
                  ` · läuft ab am ${new Date(invite.expiresAt).toLocaleDateString('de-DE')}`}
                {invite.redeemedBy &&
                  ` · eingelöst von ${invite.redeemedBy.name || invite.redeemedBy.email}`}
              </p>
            </div>
          );
        })}

        <Button variant="outline" onClick={() => navigate(routes.coach.operator.dashboard())}>
          Zurück
        </Button>
      </div>
    </AdminShell>
  );
}
